import { getPimBaseUrl, requestWithAuth } from "./pimClient";

interface CreateItemResponse {
  resource?: {
    id?: number;
  };
}

type PimErrorResponse = {
  message?: string;
  exception?: {
    message?: string;
  };
  errors?: string[] | Record<string, string[]>;
};

const buildPimUploadError = (step: string, error: unknown) => {
  const maybeAxiosError = error as {
    response?: {
      status?: number;
      data?: PimErrorResponse | string;
    };
    message?: string;
  };

  const responseStatus = maybeAxiosError?.response?.status;
  const responseData = maybeAxiosError?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return new Error(`${step} failed (HTTP ${responseStatus}): ${responseData}`);
  }

  if (
    responseData &&
    typeof responseData === "object" &&
    "exception" in responseData &&
    responseData.exception?.message
  ) {
    return new Error(
      `${step} failed (HTTP ${responseStatus}): ${responseData.exception.message}`
    );
  }

  if (
    responseData &&
    typeof responseData === "object" &&
    "message" in responseData &&
    responseData.message
  ) {
    return new Error(
      `${step} failed (HTTP ${responseStatus}): ${responseData.message}`
    );
  }

  if (maybeAxiosError?.message) {
    return new Error(`${step} failed: ${maybeAxiosError.message}`);
  }

  return new Error(`${step} failed`);
};

const getImageSchemaId = () => {
  const parsedSchemaId = Number(process.env.VUE_APP_PIM_IMAGE_SCHEMA_ID);

  if (!Number.isInteger(parsedSchemaId)) {
    throw new Error("Missing or invalid VUE_APP_PIM_IMAGE_SCHEMA_ID");
  }

  return parsedSchemaId;
};

const createImageItem = async () => {
  const uniqueId = `photo-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

  let data: CreateItemResponse | undefined;

  try {
    const response = await requestWithAuth<CreateItemResponse>({
      method: "post",
      url: "/items",
      data: {
        platform_id: uniqueId,
        name: uniqueId,
        import_schema_id: getImageSchemaId(),
        data: {},
      },
    });

    data = response.data;
  } catch (error) {
    throw buildPimUploadError("PIM image item creation", error);
  }

  const itemId = Number(data?.resource?.id);

  if (!Number.isInteger(itemId)) {
    throw new Error("PIM image item creation did not return a valid id");
  }

  return itemId;
};

const attachImageToItem = async (itemId: number, imageDataUrl: string) => {
  try {
    await requestWithAuth({
      method: "put",
      url: `/items/${itemId}/add_or_remove_image`,
      data: {
        add_transparency: false,
        file: imageDataUrl,
        tag: "cover",
      },
    });
  } catch (error) {
    throw buildPimUploadError("PIM image attach", error);
  }
};

export const uploadCapturedPhoto = async (imageDataUrl: string) => {
  if (!getPimBaseUrl()) {
    throw new Error("Missing VUE_APP_PIM_BASE_URL or VUE_APP_API_URL");
  }

  if (!imageDataUrl) {
    throw new Error("Missing image data for PIM upload");
  }

  const itemId = await createImageItem();
  await attachImageToItem(itemId, imageDataUrl);

  return itemId;
};
